import { ChangeDetectorRef, Component, ViewChild, ViewChildren } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import {
    Button,
    Content,
    IonicPage,
    ModalController,
    NavController,
    NavParams,
    PopoverController,
    Slides
} from 'ionic-angular';
import { isDefined } from 'ionic-angular/util/util';
import * as Q from 'q';
import { BaseObject } from '../../../core/business-objects/base-object/base-object.model';
import { BaseComponent } from '../../../core/components/base.component';
import { MessageBusService } from '../../../core/services/message-bus/message-bus.service';
import { isInArray } from '../../../core/util/is-in-array';
import { ENVIRONMENTS } from '../../../environments/environments';
import { ControlInlined } from '../../business-objects/control/control.model';
import { FormInlined } from '../../business-objects/form/form.model';
import { GroupInlined } from '../../business-objects/group/group.model';
import { FieldWrapperKeyboardTabEvent } from '../../modules-shared/field-wrapper/events/field-wrapper-keyboard-tab-event';
import { FieldWrapperComponent } from '../../modules-shared/field-wrapper/field-wrapper.component';
import { ProgressBarComponent } from '../../modules-shared/progress-bar/progress-bar';
import { FormEvents } from '../../services/form-helper-service/form-events';
import { FormHelperService } from '../../services/form-helper-service/form-helper.service';
import { FormResult } from '../../services/form-helper-service/form-result';
import { VasAppFacade } from '../../services/vas-app-facade.service';
import { FormEventsService } from './form-events.service';
import { FormFocusController } from './form-focus.controller';
import { FormSlidePickerComponent } from './form-slide-picker.component';
import { FormSlidesController } from './form-slides.controller';
import { FormStateService } from './form-state.service';
import { ENV } from '../../../environments/environment';

@IonicPage({defaultHistory: ['AccountsPage']})
@Component({
    selector: 'form-page',
    templateUrl: 'form.html',
    viewProviders: [
        FormStateService,
        FormEventsService,
        FormSlidesController,
        FormFocusController
    ]
})
export class FormPage extends BaseComponent {
    static readonly CLASS = 'FormPage';

    @ViewChild(Slides) slides: Slides;
    @ViewChild(Content) content: Content;
    @ViewChild(ProgressBarComponent) progressBar: ProgressBarComponent;
    @ViewChildren(FieldWrapperComponent) fieldWrappers: Array<FieldWrapperComponent>;

    @ViewChild('nextSlideButton') nextSlideButton: Button;
    @ViewChild('prevSlideButton') prevSlideButton: Button;

    data: any = {} as any;
    form: FormInlined = {} as any;
    items: Array<any> = [];
    formGroup: FormGroup = {} as any;
    formEvents: FormEvents;

    title: string = '';
    subTitle: string = '';
    showPrevButton: boolean = false;
    showNextButton: boolean = false;
    slideFromIndex: number = 0;
    slideToIndex: number = 0;
    displayingGroups: Array<GroupInlined> = [];
    targetGroupIndex: number = 0;
    activeGroupIndex: number = 0;

    assetId: string = undefined;
    formErrors: Array<string> = [];
    ionViewCanLeaveIgnore: boolean = true;
    slideTransitionSpeed = 300;

    autoFocusTimeout: any;

    constructor(
        protected appFacade: VasAppFacade,
        protected navController: NavController,
        protected navParams: NavParams,
        protected messageBusService: MessageBusService,
        protected popoverCtrl: PopoverController,
        protected slidesController: FormSlidesController,
        protected stateService: FormStateService,
        protected eventsService: FormEventsService,
        protected focusController: FormFocusController,
        protected ref: ChangeDetectorRef,
        protected modalCtrl: ModalController
    ) {
        super();
    }

    ionViewCanEnter(): Promise<any> {
        if (!this.navParams.get('formGroup')) {
            console.error('missing formGroup');
            return Promise.reject(undefined);
        }

        if (!this.navParams.get('data')) {
            console.error('missing data');
            return Promise.reject(undefined);
        }

        if (!this.navParams.get('form')) {
            console.error('missing form');
            return Promise.reject(undefined);
        }

        if (!this.navParams.get('formEvents')) {
            console.warn('no form events were passed');
            return Promise.reject(undefined);
        }

        const formEvents = this.navParams.get('formEvents') as FormEvents;

        if (!formEvents.getAccount()) {
            console.warn('account was not set in the FormEvents object');
            return Promise.reject(undefined);
        }

        return Promise.resolve();
    }

    ngOnInit(): void {

        this.formGroup = this.navParams.get('formGroup') as FormGroup;
        this.data = this.navParams.get('data') as BaseObject;
        this.form = this.navParams.get('form') as FormInlined;
        this.formEvents = this.navParams.get('formEvents') as FormEvents;
        this.messageBusService.listen(FormPage.CLASS, this);

        // we save a copy of the id, and call it assetId, so we can use this value in controls who otherwise wouldn't
        // have access to the value. e.g. the acl field
        this.assetId = this.data.id; // @todo replace this with this.formGroup.get("id").value

        // @todo move this to the constructor https://git.ironcode.io/vas/vas-mobile/issues/383
        this.appFacade.objects.account.store.setActive(this.formEvents.getAccount());

        this.stateService.setForm(this.form);
        this.slidesController.setForm(this.form);

        this.stateService.title$
            .takeUntil(this.ngUnsubscribe)
            .subscribe(value => this.title = value);

        this.stateService.subTitle$
            .takeUntil(this.ngUnsubscribe)
            .subscribe(value => this.subTitle = value);

        this.stateService.progress$
            .takeUntil(this.ngUnsubscribe)
            .subscribe(value => this.progressBar.progress = value);

        this.stateService.showPrevButton$
            .takeUntil(this.ngUnsubscribe)
            .subscribe(value => this.showPrevButton = value);

        this.stateService.showNextButton$
            .takeUntil(this.ngUnsubscribe)
            .subscribe(value => this.showNextButton = value);

        this.stateService.displayingGroups$
            .takeUntil(this.ngUnsubscribe)
            .subscribe(groups => this.displayingGroups = groups);

        this.eventsService.ionSlideDidChange
            .takeUntil(this.ngUnsubscribe)
            .subscribe(() => this.content.scrollToTop());

        this.eventsService.exitGroupBackwards
            .takeUntil(this.ngUnsubscribe)
            .subscribe(() => this.slidesController.prevSlide());

        this.eventsService.exitGroupForwards
            .takeUntil(this.ngUnsubscribe)
            .subscribe(() => this.slidesController.nextSlide());
    }

    ngAfterViewInit(): void {

        if (!this.form.groups) {
            return;
        }

        if (isInArray(ENV.name, [ENVIRONMENTS.PROD])) {
            window.onbeforeunload = e => {
                const message = 'You have attempted to leave this page. Are you sure?';
                e.returnValue = message;
                return message;
            };
        }

        this.appFacade.scheduler.pause();

        this.formGroup.valueChanges.subscribe(() => {

            this.ionViewCanLeaveIgnore = false;

            const formErrors = [];

            this.form.groups.forEach(groupDefinition => {

                groupDefinition.controls.forEach(controlDefinition => {
                    const control = this.formGroup.get([groupDefinition.name, controlDefinition.name]);

                    if (!control) {
                        console.warn('no control found for key:', `${groupDefinition.name}.${controlDefinition.name}`);
                        return;
                    }

                    if (!control.errors) return;

                    // console.log(`${groupDefinition.name}.${controlDefinition.name}`, control.errors);

                    if (controlDefinition.controlType.name !== 'hidden' && !control.touched) return;

                    Object
                        .keys(control.errors)
                        .forEach(validatorKey => {
                            formErrors.push(FormHelperService.getValidatorErrorMessage(controlDefinition.title || controlDefinition.name, validatorKey, control.errors[validatorKey]));
                        });
                });
            });

            this.formErrors = formErrors;
        });
    }

    ngAfterViewChecked(): void {
        this.focusController.setFieldWrappers(this.fieldWrappers);
        this.slidesController.setSlides(this.slides);
    }

    ionViewCanLeave(): Promise<boolean> {

        if (this.ionViewCanLeaveIgnore) {
            return Promise.resolve(true);
        }

        return this.canDiscardChanges();
    }

    ionViewWillLeave(): void {
        window.onbeforeunload = undefined;
        this.appFacade.scheduler.unpause();
    }

    onShowSlidePicker($event): void {

        const groups = this.form.groups.map(group => {
            return group.title;
        });

        const p = this.popoverCtrl.create(FormSlidePickerComponent, {
            groups
        });

        p.present({
            ev: $event
        });

        p.onDidDismiss(i => {
            this.slidesController.jumpToSlide(i);
        });
    }

    onNextSlide(): void {
        this.slidesController.nextSlide();
    }

    onPrevSlide(): void {
        this.slidesController.prevSlide();
    }

    onFieldWrapperExitBackwards(event: FieldWrapperKeyboardTabEvent): void {
        this.focusController.handleFieldWrapperExitBackwards(event);
    }

    onFieldWrapperExitForwards(event: FieldWrapperKeyboardTabEvent): void {
        this.focusController.handleFieldWrapperExitForwards(event);
    }

    onTab($event, shift): void {
        this.focusController.handleTabOnForm($event, shift);
    }

    onSubmit($event): void {
        this.ionViewCanLeaveIgnore = true;
        [
            () => this.formEvents.onBeforeSubmit(this.formGroup, this.form),
            () => this.formEvents.onSubmit(new FormResult(this.form, this.formGroup, this.data)),
            () => this.formEvents.onAfterSubmit(this.formGroup, this.form),
            () => this.navController.pop()
        ].reduce<any>(Q.when, Q())
            .catch(error => {
                this.ionViewCanLeaveIgnore = false;
                this.formEvents.onAfterSubmitFail(this.formGroup, this.form, error);
            });
    }

    onCancel($event): void {
        this.navController.pop();
    }

    lockSlides(): void {
        this.slidesController.lockSlides();
    }

    unlockSlides(): void {
        this.slidesController.unlockSlides();
    }

    canDiscardChanges(): Promise<any> {

        return new Promise((resolve, reject) => {
            if (this.formGroup.dirty) {
                [
                    () => this.appFacade.alert.yesNo(
                        'You have unsaved changes',
                        'Do you want to exit and discard your changes?'
                    ),
                    () => this.formEvents.onBeforeCancel(this.formGroup, this.form)
                ].reduce<any>(Q.when, Q()).then(
                    () => {
                        resolve();
                    },
                    () => {
                        reject();
                    }
                );
            } else {
                resolve();
            }
        });
    }

    getControl(group: GroupInlined, control: ControlInlined): AbstractControl {
        const formControl = this.formGroup.get([group.name, control.name]);

        if (!formControl) {
            console.warn('getControl returned null', group.name, control.name);
        }
        return formControl;
    }

    getOption(control: ControlInlined, option, defaultValue?): any {

        if (!isDefined(control.config)) {
            return defaultValue;
        }

        if (!isDefined(control.config[option])) {
            return defaultValue;
        }

        return control.config[option];
    }

    onFileManagerClick($event): void {
        this.modalCtrl.create('FormFileManager', { jobId: this.assetId }).present();
    }

}



// WEBPACK FOOTER //
// ./src/vas/pages/form/form.ts